import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useNewBillsAPI from "../../../api/billsAPI";
import { setFeatureData } from "../../../store/slices/appSlice";
import ValidationPopup from "../../../Components/ValidationPopup";
import ViewToggle from "./ViewToggle";
import AutomatedBillCard from "./AutomatedBillCard";
import AutomatedBillsSkeleton from "./AutomatedBillsSkeleton";
import AutomatedBillsTable from "./AutomatedBillsTable";
import {
  buildProviderAssets,
  getAutomatedToggleResponseValue,
  isDisplayableAutomatedBillValue,
  normalizeAutomatedBill,
} from "./automatedBillsUtils";

function resolveErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.PopupDescription ||
    error?.response?.data?.Message ||
    error?.message ||
    fallbackMessage
  );
}

export default function AutomatedBills({
  fetchProvider = "alfa",
  providers = [],
}) {
  const dispatch = useDispatch();
  const { GetAutomatedBills, SetAutomatedBillPaymentStatus } = useNewBillsAPI();
  const getAutomatedBillsRef = useRef(GetAutomatedBills);
  const setAutomatedBillPaymentStatusRef = useRef(SetAutomatedBillPaymentStatus);
  const [viewMode, setViewMode] = useState("grid");
  const [loadingMap, setLoadingMap] = useState({});
  const [popupState, setPopupState] = useState({
    description: "",
    open: false,
    title: "",
  });
  const automatedBillsState = useSelector(
    (state) => state?.app?.summary?.bills || {},
  );

  getAutomatedBillsRef.current = GetAutomatedBills;
  setAutomatedBillPaymentStatusRef.current = SetAutomatedBillPaymentStatus;

  const providerAssets = useMemo(
    () => buildProviderAssets(providers),
    [providers],
  );

  const normalizedBills = useMemo(
    () =>
      (Array.isArray(automatedBillsState?.automatedBills)
        ? automatedBillsState.automatedBills
        : []
      ).map((bill) => normalizeAutomatedBill(bill, providerAssets)),
    [automatedBillsState?.automatedBills, providerAssets],
  );

  const activeCount = useMemo(
    () => normalizedBills.filter((bill) => bill.isChecked).length,
    [normalizedBills],
  );

  const loadAutomatedBills = useCallback(async () => {
    try {
      await getAutomatedBillsRef.current(fetchProvider);
    } catch (error) {
      setPopupState({
        open: true,
        title: "Automated Bills Error",
        description: resolveErrorMessage(
          error,
          "Unable to load automated bills right now.",
        ),
      });
    }
  }, [fetchProvider]);

  useEffect(() => {
    loadAutomatedBills();
  }, [loadAutomatedBills]);

  const updateBillState = useCallback((billId, isChecked) => {
    const sourceRows = Array.isArray(automatedBillsState?.automatedBills)
      ? automatedBillsState.automatedBills
      : [];

    const nextRows = sourceRows.map((bill) => {
      const normalizedBill = normalizeAutomatedBill(bill, providerAssets);
      if (normalizedBill.id !== billId) return bill;

      return {
        ...bill,
        status: isChecked ? "Active" : "Paused",
        Status: isChecked ? "Active" : "Paused",
        isChecked,
        IsChecked: isChecked,
      };
    });

    dispatch(
      setFeatureData({
        section: "summary",
        key: "bills",
        value: {
          automatedBills: nextRows,
        },
      }),
    );
  }, [automatedBillsState?.automatedBills, dispatch, providerAssets]);

  const handleToggleStatus = useCallback(
    async (bill, nextChecked) => {
      if (!isDisplayableAutomatedBillValue(bill?.mobileNumber)) return;

      setLoadingMap((currentValue) => ({
        ...currentValue,
        [bill.id]: true,
      }));

      try {
        const response = await setAutomatedBillPaymentStatusRef.current(
          bill.providerKey || fetchProvider,
          {
            suyoolUserId: sessionStorage.getItem("webKey"),
            mobileNumber: bill.mobileNumber,
            checked: nextChecked,
          },
        );

        const resolvedChecked = getAutomatedToggleResponseValue(
          response,
          nextChecked,
        );
        updateBillState(bill.id, resolvedChecked);
      } catch (error) {
        setPopupState({
          open: true,
          title: "Automated Payment Error",
          description: resolveErrorMessage(
            error,
            "Unable to update the automated payment right now.",
          ),
        });
      } finally {
        setLoadingMap((currentValue) => ({
          ...currentValue,
          [bill.id]: false,
        }));
      }
    },
    [fetchProvider, updateBillState],
  );

  const isLoading = Boolean(automatedBillsState?.automatedBillsLoading);
  const hasBills = normalizedBills.length > 0;

  return (
    <>
      <section className="bills-page__automation">
        <div className="bills-page__section-header">
          <h2>Automated Bills</h2>
          <div className="bills-page__automation-controls">
            <span className="bills-page__meta-pill">{activeCount} Active</span>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <AutomatedBillsSkeleton viewMode={viewMode} />
        ) : hasBills ? (
          viewMode === "grid" ? (
            <div className="row g-3">
              {normalizedBills.map((bill) => (
                <div key={bill.id} className="col-12 col-md-6 col-xl-4">
                  <AutomatedBillCard
                    bill={bill}
                    loading={Boolean(loadingMap[bill.id])}
                    onToggleStatus={handleToggleStatus}
                  />
                </div>
              ))}
            </div>
          ) : (
            <AutomatedBillsTable
              bills={normalizedBills}
              loading={false}
              loadingMap={loadingMap}
              onToggle={handleToggleStatus}
            />
          )
        ) : (
          <div className="bills-page__empty-panel">
            <h3>No automated bills available</h3>
            <p>
              Automated bill payments will appear here once active automations are
              returned for this merchant.
            </p>
          </div>
        )}
      </section>

      <ValidationPopup
        descriptionOverride={popupState.description}
        onCloseOverride={() =>
          setPopupState({
            description: "",
            open: false,
            title: "",
          })
        }
        openOverride={popupState.open}
        titleOverride={popupState.title}
      />
    </>
  );
}
